import { useCallback, useRef, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";

/**
 * One client record (`GET /api/crm/clients/{id}`) and its header edits.
 *
 * `update(patch)` is OPTIMISTIC: the header shows the new value at once and rolls back to
 * the previous record if the server refuses (the caller reports why). The server's answer
 * replaces the local copy on success, so derived fields (display name, stage) are the
 * server's.
 */
export default function useClient(id) {
  const [client, setClient] = useState(null);
  // The record as last rendered: the rollback target of an optimistic edit.
  const current = useRef(null);
  current.current = client;
  const load = useCallback(async () => { setClient(await crmApi.getClient(id)); }, [id]);
  const { loading, loadError, reload } = useAdminLoad(load);

  const update = useCallback(async (patch) => {
    const before = current.current;
    setClient((c) => (c ? { ...c, ...patch } : c));
    try {
      const saved = await crmApi.updateClient(id, patch);
      if (saved && typeof saved === "object") setClient(saved);
      return saved;
    } catch (e) {
      setClient(before);
      throw e;
    }
  }, [id]);

  const setStage = useCallback(async (stage) => {
    const saved = await crmApi.setClientStage(id, stage);
    setClient((c) => (saved && typeof saved === "object" ? saved : { ...c, stage, stage_mode: "manual" }));
  }, [id]);

  const resumeAutomatic = useCallback(async () => {
    const saved = await crmApi.resumeClientStage(id);
    setClient((c) => (saved && typeof saved === "object" ? saved : { ...c, stage_mode: "auto" }));
  }, [id]);

  return { client, setClient, loading, loadError, reload, update, setStage, resumeAutomatic };
}
